import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type AnimationPlayState = 'idle' | 'running' | 'paused' | 'finished';

type AnimationCallbackParams = {
  playState: AnimationPlayState;
  animate: AnimateFunction;
  animation: Animation;
};

type AnimateArgs = {
  keyframes: Keyframe[];
  animationOptions?: KeyframeAnimationOptions;
  autoPlay?: boolean;
  id?: string;
  playbackRate?: number;
};

type AnimateFunction = (args: AnimateArgs) => void;

type UseAnimationsOptions = {
  ref?: MutableRefObject<HTMLElement | null>;
  id?: string;
  playbackRate?: number;
  autoPlay?: boolean;
  keyframes?: Keyframe[];
  animationOptions?: KeyframeAnimationOptions;
  onReady?: (params: AnimationCallbackParams) => void;
  onUpdate?: (params: AnimationCallbackParams) => void;
  onFinish?: (params: AnimationCallbackParams) => void;
};

type UseAnimationsReturn = {
  ref: MutableRefObject<HTMLElement | null>;
  playState: AnimationPlayState | undefined;
  getAnimation: () => Animation | undefined;
  animate: AnimateFunction;
};

const useLastValue = <T>(val: T): MutableRefObject<T> => {
  const ref = useRef(val);
  ref.current = val;
  return ref;
};

const isAnimationPlayState = (state: string): state is AnimationPlayState => {
  return state === 'idle' || state === 'running' || state === 'paused' || state === 'finished';
};

export const useAnimations = ({
  ref: refOpt,
  id,
  playbackRate,
  autoPlay,
  keyframes,
  animationOptions,
  onReady,
  onUpdate,
  onFinish,
}: UseAnimationsOptions = {}): UseAnimationsReturn => {
  const [playState, setPlayState] = useState<AnimationPlayState | undefined>(undefined);
  const hasUnmountedRef = useRef<boolean>(false);
  const animRef = useRef<Animation | undefined>(undefined);
  const prevPendingRef = useRef<boolean | undefined>(undefined);
  const prevPlayStateRef = useRef<AnimationPlayState | undefined>(undefined);
  const keyframesRef = useRef<Keyframe[] | undefined>(keyframes);
  const animationOptionsRef = useRef<KeyframeAnimationOptions | undefined>(animationOptions);
  const onReadyRef = useLastValue(onReady);
  const onUpdateRef = useLastValue(onUpdate);
  const onFinishRef = useLastValue(onFinish);
  const refVar = useRef<HTMLElement | null>(null);
  const ref = refOpt ?? refVar;

  const getAnimation = useCallback((): Animation | undefined => animRef.current, []);

  const animateCallback = useCallback<AnimateFunction>(
    function animate(args: AnimateArgs): void {
      const element = ref.current;
      if (!element || typeof element.animate !== 'function' || !args.keyframes) {
        return;
      }

      const animation = element.animate(args.keyframes, args.animationOptions);
      animRef.current = animation;

      if (args.autoPlay === false) {
        animation.pause();
      }
      if (args.id !== undefined) {
        animation.id = args.id;
      }
      if (args.playbackRate !== undefined) {
        animation.playbackRate = args.playbackRate;
      }

      const readyCallback = onReadyRef.current;
      if (readyCallback && animation.ready) {
        animation.ready
          .then((readyAnimation: Animation) => {
            const state = readyAnimation.playState;
            if (isAnimationPlayState(state)) {
              readyCallback({
                playState: state,
                animate,
                animation: readyAnimation,
              });
            }
          })
          .catch(() => {
            console.log('Animation ready promise rejected');
          });
      }

      const finishCallback = onFinishRef.current;
      if (finishCallback) {
        animation.onfinish = (event: AnimationPlaybackEvent): void => {
          const eventTarget = event.target;
          if (eventTarget instanceof Animation && !hasUnmountedRef.current) {
            const state = eventTarget.playState;
            if (isAnimationPlayState(state)) {
              finishCallback({
                playState: state,
                animate,
                animation: eventTarget,
              });
            }
          }
        };
      }

      prevPlayStateRef.current = undefined;
    },
    [onFinishRef, onReadyRef, ref],
  );

  const animate = animateCallback;

  useEffect(() => {
    const frames = keyframesRef.current;
    if (frames) {
      const args: AnimateArgs = {
        keyframes: frames,
      };

      if (id !== undefined) {
        args.id = id;
      }
      if (playbackRate !== undefined) {
        args.playbackRate = playbackRate;
      }
      if (autoPlay !== undefined) {
        args.autoPlay = autoPlay;
      }
      if (animationOptionsRef.current !== undefined) {
        args.animationOptions = animationOptionsRef.current;
      }

      animate(args);
    }
  }, [animate, autoPlay, id, playbackRate]);

  useEffect(() => {
    let rafId: number | undefined;

    const update = (): void => {
      const animation = getAnimation();

      if (animation) {
        const { pending, playState: curPlayState } = animation;

        if (prevPlayStateRef.current !== curPlayState && isAnimationPlayState(curPlayState)) {
          setPlayState(curPlayState);
        }

        const updateCallback = onUpdateRef.current;
        if (
          updateCallback &&
          (prevPendingRef.current !== pending ||
            prevPlayStateRef.current !== curPlayState ||
            curPlayState === 'running') &&
          isAnimationPlayState(curPlayState)
        ) {
          updateCallback({
            playState: curPlayState,
            animate,
            animation,
          });
        }

        prevPendingRef.current = pending;
        if (isAnimationPlayState(curPlayState)) {
          prevPlayStateRef.current = curPlayState;
        }
      }

      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);

    return (): void => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      hasUnmountedRef.current = true;

      const animation = getAnimation();
      if (animation) {
        animation.finish();
        animation.cancel();
      }
    };
  }, [animate, getAnimation, onUpdateRef]);

  return { ref, playState, getAnimation, animate };
};
